import { TableDG } from '@alison/material';
import { InitialsAvatar, radius } from '@alison/ui';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { useGridRootProps } from '@mui/x-data-grid';
import { type MouseEvent, type ReactNode, useCallback, useMemo, useState } from 'react';
import { initialsFrom } from '../../../lib/format';
import { TableEmptyState } from '../../creatives/TableEmptyState';
import { GRID_CONTAINER_SX, GRID_SX_BASE } from '../../creatives/utils';
import { CATEGORY_ICON, DEFAULT_CATEGORY_ICON } from '../../elements/data/categoryVisuals';
import type { CompetitionElementRow, CompetitionElementsData, ElementStatus } from './data/api';
import { ROW_LEVEL_DEPTH, rowPath, visibleCompetitionElementRows } from './data/tree';

const MAX_VISIBLE_AVATARS = 4;
const AVATAR_SIZE = 20;
const ELEMENT_FIELD = 'element';
const ELEMENT_COLUMN_MIN_WIDTH = 380;
const PERCENT_COLUMN_WIDTH = 120;
const COUNT_COLUMN_WIDTH = 170;
const DEFAULT_EXPANDED_DEPTH = 2;
const BOLD_TREE_LEVELS: CompetitionElementRow['rowLevel'][] = ['featureCategory', 'feature'];
const BLANK_DATA_LEVELS: CompetitionElementRow['rowLevel'][] = ['category', 'featureCategory'];
const COLUMN_LEFT_PADDING_PX = 30;
const INDENT_PER_DEPTH_PX = 18;
const EXPANDABLE_ROW_CLASS = 'competition-elements-row-expandable';

const FIXED_POPPER_SLOT_PROPS = {
  popper: {
    strategy: 'fixed' as const,
    modifiers: [{ name: 'offset', options: { offset: [-10, -25] } }],
  },
};

const STATUS_CHIP_META: Record<
  ElementStatus,
  { label: string; paletteColor: 'info' | 'warning' | 'success' }
> = {
  never_used: { label: 'Never used', paletteColor: 'info' },
  rarely_used: { label: 'Rarely used', paletteColor: 'warning' },
  well_covered: { label: 'You use it', paletteColor: 'success' },
};

interface CompetitionElementsTableGridProps {
  rows: CompetitionElementRow[];
  competitorsById: CompetitionElementsData['competitorsById'];
  loading: boolean;
  expandTags: boolean;
  onOpenCreatives: (row: CompetitionElementRow) => void;
}

function isBlankLevel(row: CompetitionElementRow): boolean {
  return BLANK_DATA_LEVELS.includes(row.rowLevel);
}

function ElementStatusChip({ status }: { status: ElementStatus }): ReactNode {
  const { label, paletteColor } = STATUS_CHIP_META[status];
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 22,
        borderRadius: `${radius.md}px`,
        px: 1,
        bgcolor: `${paletteColor}.bg`,
        color: `${paletteColor}.main`,
        fontSize: 11,
        fontWeight: 700,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

function CategoryTag({ name }: { name: string }): ReactNode {
  const theme = useTheme();
  const Icon = CATEGORY_ICON[name] ?? DEFAULT_CATEGORY_ICON;
  const accent = theme.palette.elementCategory[name] ?? theme.palette.elementCategory.default;
  return (
    <Box sx={{ alignItems: 'center', display: 'inline-flex', gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderRadius: 1,
          color: 'primary.main',
          display: 'inline-flex',
          flexShrink: 0,
          height: 28,
          justifyContent: 'center',
          width: 28,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: alpha(accent, 0.1),
          border: '1px solid',
          borderColor: alpha(accent, 0.3),
          borderRadius: 1,
          color: accent,
          display: 'inline-flex',
          minWidth: 0,
          px: 1.5,
          py: 0.5,
        }}
      >
        <Typography
          component="span"
          sx={{
            color: 'inherit',
            fontSize: 11,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Typography>
      </Box>
    </Box>
  );
}

function ElementLabel({ row }: { row: CompetitionElementRow }): ReactNode {
  if (row.rowLevel === 'category') return <CategoryTag name={row.category} />;
  if (row.rowLevel === 'tag') {
    return (
      <Box
        sx={{
          alignItems: 'center',
          color: 'text.secondary',
          display: 'flex',
          gap: 0.75,
          minWidth: 0,
          width: '100%',
        }}
      >
        <SellOutlinedIcon sx={{ flexShrink: 0, fontSize: 14 }} />
        <Tooltip title={row.tag} placement="bottom-start" slotProps={FIXED_POPPER_SLOT_PROPS}>
          <Box
            component="span"
            sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {row.tag}
          </Box>
        </Tooltip>
      </Box>
    );
  }
  const name = row.rowLevel === 'featureCategory' ? row.featureCategory : row.feature;
  return (
    <Tooltip title={name} slotProps={FIXED_POPPER_SLOT_PROPS}>
      <Box
        component="span"
        sx={{
          display: 'block',
          fontWeight: BOLD_TREE_LEVELS.includes(row.rowLevel) ? 700 : 400,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </Box>
    </Tooltip>
  );
}

function ElementCell({
  row,
  expanded,
  onToggle,
}: {
  row: CompetitionElementRow;
  expanded: boolean;
  onToggle: () => void;
}): ReactNode {
  const rootProps = useGridRootProps();
  const hasChildren = row.rowLevel !== 'tag';
  const ToggleIcon = expanded
    ? rootProps.slots.treeDataCollapseIcon
    : rootProps.slots.treeDataExpandIcon;

  function handleToggle(event: MouseEvent): void {
    onToggle();
    event.stopPropagation();
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 0.5,
        height: '100%',
        minWidth: 0,
        pl: `${COLUMN_LEFT_PADDING_PX + ROW_LEVEL_DEPTH[row.rowLevel] * INDENT_PER_DEPTH_PX}px`,
      }}
    >
      {hasChildren && (
        <rootProps.slots.baseIconButton
          size="small"
          onClick={handleToggle}
          tabIndex={-1}
          {...rootProps.slotProps?.baseIconButton}
        >
          <ToggleIcon fontSize="inherit" />
        </rootProps.slots.baseIconButton>
      )}
      <ElementLabel row={row} />
    </Box>
  );
}

function WhoUsesItCell({
  competitorIds,
  competitorsById,
}: {
  competitorIds: string[];
  competitorsById: CompetitionElementsData['competitorsById'];
}): ReactNode {
  const visibleIds = competitorIds.slice(0, MAX_VISIBLE_AVATARS);
  const hiddenIds = competitorIds.slice(MAX_VISIBLE_AVATARS);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      {visibleIds.map((competitorId, index) => {
        const competitor = competitorsById[competitorId];
        const name = competitor?.competitorName ?? competitorId;
        return (
          <Tooltip key={competitorId} title={name}>
            <InitialsAvatar
              variant="rounded"
              size={AVATAR_SIZE}
              initials={initialsFrom(name) ?? ''}
              src={competitor?.logoUrl ?? undefined}
              sx={{
                ml: index === 0 ? 0 : -1,
                border: '2px solid',
                borderColor: 'background.paper',
              }}
            />
          </Tooltip>
        );
      })}
      {hiddenIds.length > 0 && (
        <Tooltip
          title={hiddenIds
            .map((competitorId) => competitorsById[competitorId]?.competitorName ?? competitorId)
            .join(', ')}
        >
          <InitialsAvatar
            variant="rounded"
            size={AVATAR_SIZE}
            initials={`+${hiddenIds.length}`}
            sx={{ ml: -1, border: '2px solid', borderColor: 'background.paper', fontSize: 10 }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

function TopBrandCell({
  topBrand,
  competitorsById,
}: {
  topBrand: CompetitionElementRow['topBrand'];
  competitorsById: CompetitionElementsData['competitorsById'];
}): ReactNode {
  if (topBrand === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Typography color="text.disabled">—</Typography>
      </Box>
    );
  }
  const competitor = competitorsById[topBrand.competitorId];
  const name = competitor?.competitorName ?? topBrand.competitorId;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, height: '100%' }}>
      <Tooltip title={name}>
        <InitialsAvatar
          variant="rounded"
          size={AVATAR_SIZE}
          initials={initialsFrom(name) ?? ''}
          src={competitor?.logoUrl ?? undefined}
        />
      </Tooltip>
      <Typography noWrap sx={{ fontSize: 13 }}>
        {name}
      </Typography>
      <Typography color="text.secondary" sx={{ fontSize: 13 }}>
        {Math.round(topBrand.share * 100)}%
      </Typography>
    </Box>
  );
}

function elementColumn(
  expandedPaths: ReadonlySet<string>,
  onToggle: (path: string, currentlyExpanded: boolean) => void,
): GridColDef<CompetitionElementRow> {
  return {
    field: ELEMENT_FIELD,
    headerName: 'Element',
    flex: 1,
    minWidth: ELEMENT_COLUMN_MIN_WIDTH,
    valueGetter: (_value, row) =>
      ({
        category: row.category,
        featureCategory: row.featureCategory,
        feature: row.feature,
        tag: row.tag,
      })[row.rowLevel],
    renderCell: (params) => {
      const path = rowPath(params.row);
      const expanded = expandedPaths.has(path);
      return (
        <ElementCell
          row={params.row}
          expanded={expanded}
          onToggle={() => onToggle(path, expanded)}
        />
      );
    },
  };
}

function theyUseItColumn(): GridColDef<CompetitionElementRow> {
  return {
    field: 'theyUseIt',
    headerName: 'They use it',
    align: 'right',
    headerAlign: 'right',
    width: PERCENT_COLUMN_WIDTH,
    valueGetter: (_value, row) => (isBlankLevel(row) ? null : row.competitorShare),
    renderCell: (params) => {
      if (isBlankLevel(params.row)) return null;
      return (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
            {Math.round(params.row.competitorShare * 100)}%
          </Typography>
        </Box>
      );
    },
  };
}

function competitorCreativesColumn(
  onOpenCreatives: (row: CompetitionElementRow) => void,
): GridColDef<CompetitionElementRow> {
  return {
    field: 'competitorCreatives',
    headerName: "Competitors' creatives",
    align: 'center',
    headerAlign: 'center',
    width: COUNT_COLUMN_WIDTH,
    valueGetter: (_value, row) => (isBlankLevel(row) ? null : row.competitorCreatives),
    renderCell: (params) => {
      if (isBlankLevel(params.row)) return null;
      const count = params.row.competitorCreatives;
      return (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
        >
          {count === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>0</Typography>
          ) : (
            <ButtonBase
              data-test={`competition-elements-creatives-${rowPath(params.row)}`}
              aria-label={`Show ${count} competitor creatives with ${params.row.rowLevel === 'tag' ? params.row.tag : params.row.feature}`}
              onClick={(event) => {
                event.stopPropagation();
                onOpenCreatives(params.row);
              }}
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: 'primary.main',
                borderRadius: 0.5,
                px: 0.5,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {count.toLocaleString('en-US')}
            </ButtonBase>
          )}
        </Box>
      );
    },
  };
}

function whoUsesItColumn(
  competitorsById: CompetitionElementsData['competitorsById'],
): GridColDef<CompetitionElementRow> {
  return {
    field: 'whoUsesIt',
    headerName: 'Who uses it',
    align: 'center',
    headerAlign: 'center',
    minWidth: 180,
    flex: 0.8,
    valueGetter: (_value, row) => (isBlankLevel(row) ? null : row.competitors.join(', ')),
    renderCell: (params) => {
      if (isBlankLevel(params.row)) return null;
      return (
        <WhoUsesItCell competitorIds={params.row.competitors} competitorsById={competitorsById} />
      );
    },
  };
}

function topBrandColumn(
  competitorsById: CompetitionElementsData['competitorsById'],
): GridColDef<CompetitionElementRow> {
  return {
    field: 'topBrand',
    headerName: 'Top brand',
    minWidth: 200,
    flex: 1,
    valueGetter: (_value, row) => (isBlankLevel(row) ? null : (row.topBrand?.competitorId ?? '')),
    renderCell: (params) => {
      if (isBlankLevel(params.row)) return null;
      return <TopBrandCell topBrand={params.row.topBrand} competitorsById={competitorsById} />;
    },
  };
}

function youUseItColumn(): GridColDef<CompetitionElementRow> {
  return {
    field: 'youUseIt',
    headerName: 'You use it',
    align: 'right',
    headerAlign: 'right',
    width: PERCENT_COLUMN_WIDTH + 40,
    valueGetter: (_value, row) => (isBlankLevel(row) ? null : row.clientShare),
    renderCell: (params) => {
      if (isBlankLevel(params.row)) return null;
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            height: '100%',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
            {Math.round(params.row.clientShare * 100)}%
          </Typography>
          {params.row.rowLevel === 'tag' && <ElementStatusChip status={params.row.status} />}
        </Box>
      );
    },
  };
}

export function CompetitionElementsTableGrid({
  rows,
  competitorsById,
  loading,
  expandTags,
  onOpenCreatives,
}: CompetitionElementsTableGridProps): ReactNode {
  const [expansionOverrides, setExpansionOverrides] = useState<Record<string, boolean>>({});
  const [expandTagsFor, setExpandTagsFor] = useState(expandTags);
  if (expandTags !== expandTagsFor) {
    setExpandTagsFor(expandTags);
    setExpansionOverrides({});
  }

  const toggleExpansion = useCallback((path: string, currentlyExpanded: boolean): void => {
    setExpansionOverrides((current) => ({ ...current, [path]: !currentlyExpanded }));
  }, []);

  const defaultExpandedDepth = expandTags ? ROW_LEVEL_DEPTH.tag : DEFAULT_EXPANDED_DEPTH;

  const expandedPaths = useMemo(() => {
    const expanded = new Set<string>();
    for (const row of rows) {
      const path = rowPath(row);
      if (expansionOverrides[path] ?? ROW_LEVEL_DEPTH[row.rowLevel] < defaultExpandedDepth) {
        expanded.add(path);
      }
    }
    return expanded;
  }, [rows, expansionOverrides, defaultExpandedDepth]);

  const visibleRows = useMemo(
    () => visibleCompetitionElementRows(rows, expandedPaths),
    [rows, expandedPaths],
  );

  const columns = useMemo<GridColDef<CompetitionElementRow>[]>(
    () => [
      elementColumn(expandedPaths, toggleExpansion),
      theyUseItColumn(),
      competitorCreativesColumn(onOpenCreatives),
      whoUsesItColumn(competitorsById),
      topBrandColumn(competitorsById),
      youUseItColumn(),
    ],
    [expandedPaths, toggleExpansion, competitorsById, onOpenCreatives],
  );

  function handleRowClick(params: GridRowParams<CompetitionElementRow>): void {
    if (params.row.rowLevel === 'tag') return;
    const path = rowPath(params.row);
    toggleExpansion(path, expandedPaths.has(path));
  }

  return (
    <Box sx={GRID_CONTAINER_SX}>
      <TableDG
        columns={columns}
        rows={visibleRows}
        getRowId={(row: CompetitionElementRow) => rowPath(row)}
        getRowClassName={(params) => (params.row.rowLevel === 'tag' ? '' : EXPANDABLE_ROW_CLASS)}
        onRowClick={handleRowClick}
        disableColumnSorting
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        loading={loading}
        sx={[GRID_SX_BASE, { [`& .${EXPANDABLE_ROW_CLASS}`]: { cursor: 'pointer' } }]}
        slots={{ noRowsOverlay: TableEmptyState }}
        slotProps={{ noRowsOverlay: { children: 'No elements match these filters' } }}
      />
    </Box>
  );
}
